import { Component, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { EChartOption } from "echarts";
import { LeadTimeStatusEnum } from "@shared/enums/lead-time-status-.enum";
import GenerateDataZoomUtil from "@shared/utils/data-zoom.util";
import { commonChartTooltip } from "@shared/variables/echart-common.variable";
import { WidgetStatePlaceholderEnum } from "@shared/enums/widget-state-placeholder.enum";
import { CustomEventService } from "@shared/services/custom-event.service";
import { WidgetService } from "@shared/services/widget.service";
import ActionTypesEnum from "@shared/enums/action-constant.enum";
import { take } from "rxjs/operators";

@UntilDestroy()
@Component({
  selector: 'app-deployment-analytics-widget',
  templateUrl: './deployment-analytics-widget.component.html'
})
export class DeploymentAnalyticsWidgetComponent implements OnInit, OnDestroy {

  @Input() dateFilterable: boolean;
  widgetId: string;
  widgetName: string;
  widgetType: string;
  widgetFooter: Widget.FooterModel;
  headerAddon: string;
  headerComponents: any[] = [];
  selectedDate: string;
  note: string;
  url: string;
  from: number;
  showDownloadIcon: boolean;

  loading: boolean;
  initialLoading: boolean;
  widgetStatePlaceholderEnum = WidgetStatePlaceholderEnum;
  showWidgetStatePlaceholder: WidgetStatePlaceholderEnum;

  deploymentData: any;
  chartOption: EChartOption;

  date: any;
  leadTimeEnum = LeadTimeStatusEnum;
  fields: any;

  leadTimeValueUp: any;
  deploymentFrequencyValueUp: any;
  changeFailureValueUp: any;
  meanTimeToRecoveryValueUp: any;

  leadTimeCalc: any;
  deploymentFrequencyCalc: any;
  meanTimeCalc:any;
  changeFailureCalc:any;

  leadTimeDiff : any;
  leadTimeDiffTooltip: any;
  deploymentFrequencyDiff: any;
  deploymentFrequencyDiffTooltip: any;
  meanTimeDiff : any;
  meanTimeDiffTooltip : any;
  changeFailureDiff : any;
  changeFailureDiffTooltip: any;

  leadTimePreviousDeploymentExists: any;
  deploymentFrequencyPreviousDeploymentExists: any;
  changeFailurePreviousDeploymentExists: any;
  meanTimeToRecoveryPreviousDeploymentExists: any;

  @ViewChild('toolTipTitle') toolTipTitle: TemplateRef<any>;
  toolTipHTML: string;

  constructor(private customEventService: CustomEventService,
              private widgetService: WidgetService) {
  }

  ngOnInit(): void {
    this.loadWidgetData(true);


    this.customEventService.eventAnnounced$.pipe(
      untilDestroyed(this)
    ).subscribe(({ action, data }) => {
      if (action === ActionTypesEnum.UPDATE_WIDGET_DATA) {
        this.loadWidgetData();
      }

      if (action === ActionTypesEnum.UPDATE_WIDGET_DATE) {
        const { id, date } = data;

        if (this.widgetId === id ) {
          this.selectedDate = date;
          this.loadWidgetData();
        }
      }
    });
  }

  loadWidgetData(initial?: boolean): void{
   this.loading = true;

    if (initial) {
      this.initialLoading = true;
    }

    this.headerComponents = [];

    this.widgetService.get(this.widgetId, this.selectedDate)
      .pipe(untilDestroyed(this), take(1))
      .subscribe(response => {
        this.initialLoading = false;
        if (!response.data) {
          this.showWidgetStatePlaceholder = this.widgetStatePlaceholderEnum.DATA_NOT_FOUND;
          return;
        }

        this.showWidgetStatePlaceholder = undefined;
        this.deploymentData = response.data;
        this.leadTimeValueUp = this.deploymentData.leadTimeWidgetData.leadTimeValueUp;
        this.deploymentFrequencyValueUp = this.deploymentData.frequencyWidgetData.deploymentFrequencyValueUp;
        this.changeFailureValueUp = this.deploymentData.changeFailureRateWidgetData.changeFailureValueUp;
        this.meanTimeToRecoveryValueUp = this.deploymentData.timeToRestoreWidgetData.meanTimeToRecoveryValueUp;

        this.leadTimeDiff = this.deploymentData.leadTimeWidgetData.leadTimeDifference;
        this.leadTimeDiffTooltip =  this.deploymentData.leadTimeWidgetData.leadTimeDifferenceTooltip;
        this.deploymentFrequencyDiff = this.deploymentData.frequencyWidgetData.deploymentFrequencyDifference;
        this.deploymentFrequencyDiffTooltip = this.deploymentData.frequencyWidgetData.deploymentFrequencyDifferenceTooltip;
        this.changeFailureDiff = this.deploymentData.changeFailureRateWidgetData.changeFailureDifference;
        this.changeFailureDiffTooltip = this.deploymentData.changeFailureRateWidgetData.changeFailureDifferenceTooltip;
        this.meanTimeDiff = this.deploymentData.timeToRestoreWidgetData.meanTimeToRecoveryDifference;
        this.meanTimeDiffTooltip = this.deploymentData.timeToRestoreWidgetData.meanTimeToRecoveryDifferenceTooltip;

        this.leadTimeCalc = this.deploymentData.leadTimeWidgetData.leadTimeDifference?.toString().includes('-');
        this.deploymentFrequencyCalc = this.deploymentData.frequencyWidgetData.deploymentFrequencyDifference?.toString().includes('-');
        this.changeFailureCalc = this.deploymentData.changeFailureRateWidgetData.changeFailureDifference?.toString().includes('-');
        this.meanTimeCalc = this.deploymentData.timeToRestoreWidgetData.meanTimeToRecoveryDifference?.toString().includes('-');

        this.leadTimePreviousDeploymentExists = this.deploymentData.leadTimeWidgetData.previousDeploymentExists;
        this.deploymentFrequencyPreviousDeploymentExists = this.deploymentData.frequencyWidgetData.previousDeploymentExists;
        this.changeFailurePreviousDeploymentExists = this.deploymentData.changeFailureRateWidgetData.previousDeploymentExists;
        this.meanTimeToRecoveryPreviousDeploymentExists = this.deploymentData.timeToRestoreWidgetData.previousDeploymentExists;

        this.deploymentData.deploymentAnalyticsInfo.forEach((value) => {
          this.headerComponents.push(value.label);
        });


        this.loadChartData();

        if (response.footer) {
          this.widgetFooter = response.footer;
        }

        this.loading = false;
      }, error => {
        console.log('error while fetching widget data', error);
        this.loading = false;
        this.initialLoading = false;
        this.showWidgetStatePlaceholder = this.widgetStatePlaceholderEnum.DATA_NOT_FOUND;
      });
  }

  loadChartData(): void {
    if (!this.deploymentData.frequencyWidgetData.series) {
      return;
    }

    const tooltipData = this.deploymentData.frequencyWidgetData;
    const formatter = (value): string => {
      const dataIndex = value[0].dataIndex;
      const data = tooltipData.tooltip[dataIndex];

      return `<div style="text-align: left; font-size: 12px;">${data.timeInterval}<br>
               <div style="border-bottom: 1px dashed #000; margin: 3px 0"></div>
                <span>deployment frequency</span><br>
                ${data.deploymentsForInterval}</div>`;
    };

    const dataZoom = () => {
      if (this.deploymentData.frequencyWidgetData.xAxis.size >= 10) {
        return GenerateDataZoomUtil.generateDataZoom(
          {
            dataCount: this.deploymentData.frequencyWidgetData.xAxis.size,
            orient: 'horizontal',
            showDataShadow: true,
            left: 75,
            right: 50,
            height: 25,
            x: 30
          });
      } else {
        return [];
      }
    };

        if (!this.deploymentData.frequencyWidgetData.series) {
          return;
        }
        this.chartOption = {
          dataZoom: dataZoom(),
          tooltip: {
            ...commonChartTooltip,
            trigger: 'axis',
            formatter
          },
          xAxis: {
            type: 'category',
            data: this.deploymentData.frequencyWidgetData.xAxis
          },
          yAxis: {
            type: 'value'
          },
          series: [
            {
              data: this.deploymentData.frequencyWidgetData.series,
              type: 'bar',
              itemStyle: {
                color: '#96cccb'
              }
            }
          ]
        };
  }

  ngOnDestroy(): void { }
}
